import { createReducer, on } from '@ngrx/store';
import { mergeSurveyPatch, patchSurveyInList } from '@ps/survey-utils';
import { ImpersonationActions } from '@ps/auth-data-access';

import {
  ManageSurveyActions,
  ManageSurveyApiActions,
} from './manage-survey.actions';
import {
  INITIAL_MANAGE_SURVEY_STATE,
  QUOTAS_PAGE_LIMIT,
} from '../survey-state.constants';
import { ManageSurveyState } from '../survey-state.model';

export const manageSurveyReducer = createReducer(
  INITIAL_MANAGE_SURVEY_STATE,

  on(
    ManageSurveyActions.loadSurveyDetails,
    (state): ManageSurveyState => ({
      ...state,
      loading: true,
      error: null,
    }),
  ),

  on(
    ManageSurveyActions.resetSurveyDetails,
    (): ManageSurveyState => INITIAL_MANAGE_SURVEY_STATE,
  ),

  on(
    ManageSurveyApiActions.loadSurveySuccess,
    (state, { survey }): ManageSurveyState => ({
      ...state,
      survey,
      loading: false,
      error: null,
    }),
  ),

  on(
    ManageSurveyApiActions.loadSurveyFailure,
    (state, { error }): ManageSurveyState => ({
      ...state,
      survey: null,
      loading: false,
      error,
    }),
  ),

  on(
    ManageSurveyActions.loadChildSurveyDetails,
    (state): ManageSurveyState => ({
      ...state,
      childSurveyLoading: true,
      childSurveyError: null,
    }),
  ),

  on(
    ManageSurveyApiActions.loadChildSurveySuccess,
    (state, { survey }): ManageSurveyState => ({
      ...state,
      childSurvey: survey,
      childSurveyLoading: false,
      childSurveyError: null,
    }),
  ),

  on(
    ManageSurveyApiActions.loadChildSurveyFailure,
    (state, { error }): ManageSurveyState => ({
      ...state,
      childSurvey: null,
      childSurveyLoading: false,
      childSurveyError: error,
    }),
  ),

  on(
    ManageSurveyActions.loadSurveyQuotas,
    (state): ManageSurveyState => ({
      ...state,
      loading: true,
      error: null,
    }),
  ),

  on(
    ManageSurveyActions.loadMoreQuotas,
    (state): ManageSurveyState =>
      // Ignore while a page is already in flight so a rapid second request
      // can't advance past — and skip — the page being fetched.
      state.quotasLoadingMore
        ? state
        : {
            ...state,
            quotaLoadedPage: state.quotaLoadedPage + 1,
            quotasLoadingMore: true,
            error: null,
          },
  ),

  on(
    ManageSurveyActions.loadAllQuotas,
    (state): ManageSurveyState => ({
      ...state,
      quotasLoadingMore: true,
      error: null,
    }),
  ),

  on(
    ManageSurveyActions.resetSurveyQuotas,
    (state): ManageSurveyState => ({
      ...state,
      quotas: [],
      quotaTotal: 0,
      quotaLoadedPage: 0,
      quotasLoadingMore: false,
      loading: false,
      error: null,
      quotaLockingIds: [],
      quotaSavingIds: [],
    }),
  ),

  on(
    ManageSurveyApiActions.loadSurveyQuotasSuccess,
    (state, { quotas, total }): ManageSurveyState => ({
      ...state,
      quotas,
      quotaTotal: total,
      quotaLoadedPage: 1,
      loading: false,
      error: null,
    }),
  ),

  on(
    ManageSurveyApiActions.loadSurveyQuotasFailure,
    (state, { error }): ManageSurveyState => ({
      ...state,
      quotas: [],
      quotaTotal: 0,
      quotaLoadedPage: 0,
      loading: false,
      error,
    }),
  ),

  on(
    ManageSurveyApiActions.updateCurrentCpiSuccess,
    (state, { survey }): ManageSurveyState => ({
      ...state,
      survey,
      error: null,
    }),
  ),

  on(
    ManageSurveyApiActions.updateChildCurrentCpiSuccess,
    (state, { survey }): ManageSurveyState => ({
      ...state,
      childSurvey: survey,
      childSurveyLoading: false,
      childSurveyError: null,
    }),
  ),

  on(
    ManageSurveyApiActions.loadMoreQuotasSuccess,
    (state, { quotas }): ManageSurveyState => ({
      ...state,
      quotas: [...state.quotas, ...quotas],
      quotasLoadingMore: false,
      error: null,
    }),
  ),

  on(
    ManageSurveyApiActions.updateCurrentCpiFailure,
    (state): ManageSurveyState => state,
  ),

  on(
    ManageSurveyApiActions.loadMoreQuotasFailure,
    (state): ManageSurveyState => ({
      ...state,
      // Roll back the page advanced by loadMoreQuotas so a retry refetches it.
      quotaLoadedPage: state.quotaLoadedPage - 1,
      quotasLoadingMore: false,
    }),
  ),

  on(
    ManageSurveyApiActions.loadAllQuotasSuccess,
    (state, { quotas }): ManageSurveyState => ({
      ...state,
      quotas,
      // The response holds every quota, so the count is authoritative — don't
      // depend on a `total` header the unpaginated endpoint may not send.
      quotaTotal: quotas.length,
      quotaLoadedPage: Math.ceil(quotas.length / QUOTAS_PAGE_LIMIT),
      quotasLoadingMore: false,
      error: null,
    }),
  ),

  on(
    ManageSurveyApiActions.loadAllQuotasFailure,
    (state): ManageSurveyState => ({
      ...state,
      quotasLoadingMore: false,
    }),
  ),

  on(
    ManageSurveyActions.setQuotaLock,
    (state, { psQuotaId }): ManageSurveyState => ({
      ...state,
      quotaLockingIds: [...state.quotaLockingIds, psQuotaId],
      error: null,
    }),
  ),

  on(
    ManageSurveyApiActions.setQuotaLockSuccess,
    (state, { psQuotaId, locked }): ManageSurveyState => ({
      ...state,
      quotas: state.quotas.map((quota) =>
        quota.psQuotaId === psQuotaId ? { ...quota, locked } : quota,
      ),
      error: null,
      quotaLockingIds: state.quotaLockingIds.filter((id) => id !== psQuotaId),
    }),
  ),

  on(
    ManageSurveyApiActions.setQuotaLockFailure,
    (state, { psQuotaId }): ManageSurveyState => ({
      ...state,
      quotaLockingIds: state.quotaLockingIds.filter((id) => id !== psQuotaId),
    }),
  ),

  on(
    ManageSurveyActions.setQuotaCurrentTarget,
    (state, { psQuotaId }): ManageSurveyState => ({
      ...state,
      quotaSavingIds: [...state.quotaSavingIds, psQuotaId],
      error: null,
    }),
  ),

  on(
    ManageSurveyApiActions.setQuotaCurrentTargetSuccess,
    (state, { psQuotaId, update }): ManageSurveyState => ({
      ...state,
      quotas: state.quotas.map((quota) =>
        quota.psQuotaId === psQuotaId
          ? {
              ...quota,
              goal: update.goal,
              currentlyOpen: update.currentlyOpen,
              maximum: update.maximum,
              allocationPercentage: update.allocationPercentage,
              fielded: update.fielded,
              locked: update.locked,
              suppliers: update.suppliers,
            }
          : quota,
      ),
      error: null,
      quotaSavingIds: state.quotaSavingIds.filter((id) => id !== psQuotaId),
    }),
  ),

  on(
    ManageSurveyApiActions.setQuotaCurrentTargetFailure,
    (state, { psQuotaId }): ManageSurveyState => ({
      ...state,
      quotaSavingIds: state.quotaSavingIds.filter((id) => id !== psQuotaId),
    }),
  ),

  on(
    ManageSurveyActions.loadTrafficChannels,
    (state): ManageSurveyState => ({
      ...state,
      trafficChannelsLoading: true,
      trafficChannelsError: null,
    }),
  ),

  on(
    ManageSurveyActions.resetTrafficChannels,
    (state): ManageSurveyState => ({
      ...state,
      trafficChannels: [],
      trafficChannelsLoading: false,
      trafficChannelsError: null,
      tcStatusChangingIds: [],
    }),
  ),

  on(
    ManageSurveyApiActions.loadTrafficChannelsSuccess,
    (state, { trafficChannels }): ManageSurveyState => ({
      ...state,
      trafficChannels,
      trafficChannelsLoading: false,
      trafficChannelsError: null,
    }),
  ),

  on(
    ManageSurveyApiActions.loadTrafficChannelsFailure,
    (state, { error }): ManageSurveyState => ({
      ...state,
      trafficChannels: [],
      trafficChannelsLoading: false,
      trafficChannelsError: error,
    }),
  ),

  on(
    ManageSurveyActions.setTrafficChannelStatus,
    (state, { surveyId }): ManageSurveyState => ({
      ...state,
      tcStatusChangingIds: [...state.tcStatusChangingIds, surveyId],
      trafficChannelsError: null,
    }),
  ),

  on(
    ManageSurveyApiActions.setTrafficChannelStatusSuccess,
    (state, { surveyId, status }): ManageSurveyState => ({
      ...state,
      trafficChannels: state.trafficChannels.map((channel) =>
        channel.id === surveyId ? { ...channel, status } : channel,
      ),
      trafficChannelsError: null,
      tcStatusChangingIds: state.tcStatusChangingIds.filter(
        (id) => id !== surveyId,
      ),
    }),
  ),

  on(
    ManageSurveyApiActions.setTrafficChannelStatusFailure,
    (state, { surveyId }): ManageSurveyState => ({
      ...state,
      tcStatusChangingIds: state.tcStatusChangingIds.filter(
        (id) => id !== surveyId,
      ),
    }),
  ),

  on(
    ManageSurveyActions.launchTrafficChannel,
    (state): ManageSurveyState => ({
      ...state,
      creatingTrafficChannel: true,
      trafficChannelCreationError: null,
    }),
  ),

  on(
    ManageSurveyApiActions.launchTrafficChannelSuccess,
    (state, { trafficChannels }): ManageSurveyState => ({
      ...state,
      trafficChannels,
      creatingTrafficChannel: false,
      psidUploadLoading: false,
      psidUploadError: null,
      psidUploadFilePath: null,
      psidUploadUniqCount: null,
      psidUploadTotalCount: null,
    }),
  ),

  on(
    ManageSurveyApiActions.launchTrafficChannelFailure,
    (state, { error }): ManageSurveyState => ({
      ...state,
      creatingTrafficChannel: false,
      trafficChannelCreationError: error,
    }),
  ),

  on(
    ManageSurveyActions.uploadPsidFile,
    (state): ManageSurveyState => ({
      ...state,
      psidUploadLoading: true,
      psidUploadError: null,
    }),
  ),

  on(
    ManageSurveyApiActions.uploadPsidFileSuccess,
    (
      state,
      { filePath, uniqPsidCount, uploadedPsidCount },
    ): ManageSurveyState => ({
      ...state,
      psidUploadLoading: false,
      psidUploadError: null,
      psidUploadFilePath: filePath,
      psidUploadUniqCount: uniqPsidCount,
      psidUploadTotalCount: uploadedPsidCount,
    }),
  ),

  on(
    ManageSurveyApiActions.uploadPsidFileFailure,
    (state, { error }): ManageSurveyState => ({
      ...state,
      psidUploadLoading: false,
      psidUploadError: error,
      psidUploadFilePath: null,
      psidUploadUniqCount: null,
      psidUploadTotalCount: null,
    }),
  ),

  on(
    ManageSurveyActions.clearPsidFile,
    (state): ManageSurveyState => ({
      ...state,
      psidUploadLoading: false,
      psidUploadError: null,
      psidUploadFilePath: null,
      psidUploadUniqCount: null,
      psidUploadTotalCount: null,
      creatingTrafficChannel: false,
      trafficChannelCreationError: null,
    }),
  ),

  on(
    ImpersonationActions.contextSettled,
    (): ManageSurveyState => INITIAL_MANAGE_SURVEY_STATE,
  ),

  on(
    ManageSurveyActions.updateSurvey,
    (state, { surveyId, survey }): ManageSurveyState => ({
      ...state,
      survey:
        state.survey?.id === surveyId
          ? mergeSurveyPatch(state.survey, survey)
          : state.survey,
      // Traffic channels are surveys too; patch a matching channel so the
      // traffic tab reflects live socket updates.
      trafficChannels: patchSurveyInList(
        state.trafficChannels,
        surveyId,
        survey,
      ),
      error: null,
    }),
  ),
);
